import { parseReturnPath } from '@highlight-cp/auth-client/return-path';
import { CenteredAbsoluteLoader } from '@highlight-cp/core-ui-compositions';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useClientPortalAuth } from '../../../auth/ClientPortalAuthProvider';

export const useAuth = () => {
  const auth = useClientPortalAuth();

  return {
    email: auth.status === 'authenticated' ? auth.session.email : undefined,
    status: auth.status,
  };
};

export function AuthGuard({ children }: { children: ReactNode }) {
  const router = useRouter();

  const { email, status } = useAuth();
  const unauthenticatedPathRegex = /^\/preview|\/use-cases/i;
  const allowedUrls = [
    '/',
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-success',
    '/sample-size-calculator',
    '/stat-significance-calculator',
    '/testing-method-selector',
    '/roi-calculator',
  ];

  const currentPathname = router.pathname;
  const isHeimdallResetPasswordPath = currentPathname === '/auth/reset-password/heimdall';
  const isAllowedUrl = allowedUrls.includes(currentPathname) || isHeimdallResetPasswordPath;
  // Unknown URLs land on the catch-all 404, which has its own signed-out version. It isn't an
  // allowed URL: those send signed-in visitors on to /home.
  const isNotFoundPath = currentPathname === '/[...notFound]';

  //TODO: Fix dependencies on useEffect. See CP-3913.
  useEffect(() => {
    const isUnauthenticatedPath = router.asPath.match(unauthenticatedPathRegex);

    // A wrong role keeps its Heimdall session; the login page says to switch accounts.
    if (
      (status === 'unauthenticated' || status === 'unauthorized') &&
      !isUnauthenticatedPath &&
      !isNotFoundPath
    ) {
      if (!email && !isAllowedUrl) {
        const redirect = encodeURIComponent(router.asPath);
        void router.replace(`/auth/login?redirect=${redirect}`);
      }
    }

    // After authentication, if we're on an allowed page (like /auth/login),
    // let the login flow handle the redirect via the `redirect` query param.
    // Skip redirect if we're on the Heimdall reset-password page to let that flow handle it
    if (
      status === 'authenticated' &&
      isAllowedUrl &&
      !isHeimdallResetPasswordPath &&
      currentPathname !== '/sample-size-calculator' &&
      currentPathname !== '/stat-significance-calculator'
    ) {
      void router.replace(parseReturnPath(router.query?.redirect, '/auth/login') ?? '/home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, status, router.asPath]);

  if (status === 'loading') {
    return <CenteredAbsoluteLoader dataTestId="auth-guard-loader" />;
  }

  if (email || isAllowedUrl || isNotFoundPath || router.asPath.match(unauthenticatedPathRegex)) {
    return children;
  }

  return null;
}
